import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
}

/**
 * Last-resort boundary for render-time failures. It deliberately renders no diagnostic detail: an error
 * surface on a PHI-handling screen must not expose values or stack traces to the provider.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Browser errors are not forwarded to Application Insights: the portal emits no client-side
    // telemetry, so nothing here may carry provider data off the page.
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div role="alert" className="mx-auto max-w-lg p-8 text-center">
          <h1 className="text-lg font-semibold">Something went wrong.</h1>
          <p className="mt-2 text-sm text-muted">
            Please reload the page. If the problem continues, contact Bankers Fidelity provider
            services.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}
